import React from "react";
import { useEffect, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import {
  createBnfPatient,
  getFinancerPlans,
} from "../services/patientsService";
import { useStateAuthValue } from "../context/AuthState";
import { useToast } from "../context/Toast/ToastProvider";
import { showToast, ToastType } from "../utils/ToastUtil";
import { Form, Formik } from "formik";
import { FormikField, SelectObjectField } from "../components/FormFields";
import * as Yup from "yup";
import { BaseModal } from "../components/Modals/BaseModal";
import { Skeleton } from "@material-ui/lab";

const CreatePatient = ({ open, setOpen, getPatientsData }) => {
  const [{ userToken }] = useStateAuthValue();
  const toastDispatch = useToast();
  const [patient] = useState({
    firstName: "",
    lastName: "",
    id_number: "",
    financer_plan: null,
    email: "",
    sex: "",
    phone: "",
  });
  const FormScheme = Yup.object().shape({
    firstName: Yup.string().trim().required("No debe quedar vacío"),
    lastName: Yup.string().trim().required("No debe quedar vacío"),
    id_number: Yup.number("El dni solo puede contener numeros")
      .min(999999, "El DNI debe tener minimo 7 digitos")
      .required("No debe quedar vacío"),
    financer_plan: Yup.string().required("No debe quedar vacío"),
    email: Yup.string()
      .trim()
      .email("El correo no tiene un formato válido")
      .required("No debe quedar vacío"),
    sex: Yup.string().required("No debe quedar vacío"),
    phone: Yup.string(),
  });
  const [financerPlansList, setFinancerPlansList] = useState([]);
  const [currentFinancerPlan, setCurrentFinancerPlan] = useState({});
  const sexList = [
    {
      name: `Hombre`,
      id: `MALE`,
    },
    {
      name: `Mujer`,
      id: `FEMALE`,
    },
    {
      name: `No binario`,
      id: `NON_BINARY`,
    },
  ];
  const showError = (msgError) => {
    showToast(toastDispatch, msgError, ToastType.ERROR, ToastType.ERROR);
  };
  const fetchFinancerPlans = async () => {
    const response = await getFinancerPlans(userToken, showError);
    let _financerPlansList = [];
    response?.data?.map((plan) => {
      if (plan.financerId == "32333636-3631-3338-2d33-3536362d3336") {
        _financerPlansList = [
          ..._financerPlansList,
          {
            name: `${plan.planName}`,
            id: plan.id,
          },
        ];
      }
    });
    let _agreementFinancerPlansList = [];
    response?.data?.map((plan) => {
      if (plan.financerId != "32333636-3631-3338-2d33-3536362d3336") {
        _agreementFinancerPlansList = [
          ..._agreementFinancerPlansList,
          {
            name: `${plan.planName} - ${plan.financer.financerName}`,
            id: plan.id,
          },
        ];
      }
    });
    _agreementFinancerPlansList.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
    _financerPlansList = [
      ..._financerPlansList,
      ..._agreementFinancerPlansList,
    ].filter((plan) => plan.name.toUpperCase().includes("BNF"));
    setFinancerPlansList(_financerPlansList);
  };

  useEffect(() => {
    setLoadingData(true);
    fetchFinancerPlans().finally(() => setLoadingData(false));
  }, []);

  useEffect(() => {
    setCurrentFinancerPlan({});
  }, [open]);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  // El endpoint sólo acepta estos 7 campos; cualquier extra se ignora.
  // `sex` nunca puede viajar como "": el back no llega a deserializar el JSON y
  // devuelve un error opaco, así que sin elegir va null.
  const buildPayload = (values) => {
    const payload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      id_number: String(values.id_number).trim(),
      email: values.email.trim(),
      sex: values.sex || null,
      financer_plan: currentFinancerPlan?.id || values.financer_plan,
    };
    const phone = values.phone?.trim();
    if (phone) {
      payload.phone = phone;
    }
    return payload;
  };

  const handleCreatePatient = async (values, { setFieldError }) => {
    setLoading(true);
    try {
      const result = await createBnfPatient(userToken, buildPayload(values));
      if (result.ok) {
        showToast(
          toastDispatch,
          "Se creó con éxito",
          ToastType.SUCCESS,
          ToastType.SUCCESS
        );
        setOpen(false);
        return;
      }
      if (result.error.tipo === "campos") {
        // Los mensajes vienen del back ya redactados para el usuario final y
        // `field` coincide con el nombre del input: se muestran tal cual.
        result.error.errores.forEach((fieldError) => {
          setFieldError(fieldError.field, fieldError.message);
        });
        showToast(
          toastDispatch,
          result.error.errores[0].message,
          ToastType.ERROR,
          ToastType.ERROR
        );
      } else {
        showError(result.error.mensaje);
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <BaseModal
      open={open}
      setOpen={(param) => {
        if (loading) {
          return;
        }
        setOpen(param);
      }}
    >
      <h3>Crear nuevo usuario</h3>
      <Formik
        onSubmit={(values, helpers) => {
          handleCreatePatient(values, helpers);
          //getPatientsData();
        }}
        initialValues={patient}
        validationSchema={FormScheme}
      >
        {({ values, setValues, errors }) => (
            <Form>
              {loadingData ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2,1fr)",
                    gridGap: "25px",
                  }}
                >
                  {[...Array(7)].map((_, i) => (
                    <Skeleton key={i} variant="rect" height={56} />
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2,1fr)",
                    gridGap: "25px",
                  }}
                >
                  <FormikField name="lastName" label="Apellido" />
                  <FormikField name="firstName" label="Nombre" />
                  <FormikField
                    name="id_number"
                    label="DNI"
                    type="number"
                  />
                  <FormikField
                    name="email"
                    label="Correo"
                  />
                  <FormikField
                    name="phone"
                    label="Teléfono (opcional)"
                  />
                  <SelectObjectField
                    label="Plan"
                    setValue={(e) => {
                      setValues({ ...values, financer_plan: e.id });
                      setCurrentFinancerPlan(e);
                    }}
                    value={currentFinancerPlan}
                    data={financerPlansList || []}
                    className="field"
                    errorMsg={errors.financer_plan}
                    isError={errors.financer_plan}
                  />
                  <SelectObjectField
                    label="Sexo"
                    setValue={(e) => {
                      setValues({ ...values, sex: e.id });
                    }}
                    value={sexList.filter((fp) => fp.id == values.sex)[0]}
                    data={sexList}
                    className="field"
                    errorMsg={errors.sex}
                    isError={errors.sex}
                  />
                </div>
              )}
              <div style={{ display: "flex", marginTop: "30px" }}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  style={{
                    backgroundColor: "#DA2037",
                    color: "#fff",
                    margin: "0 10px 0 0",
                    borderRadius: "20px",
                    textTransform: "capitalize",
                    opacity: loading ? 0.6 : 1,
                  }}
                  onClick={() => setOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  // Mientras el alta está en vuelo el botón queda deshabilitado:
                  // el back no es idempotente y un segundo submit crearía un
                  // duplicado (o devolvería "Ya existe un paciente con el email").
                  disabled={
                    loading ||
                    loadingData ||
                    Object.keys(errors).length != 0
                  }
                  type="submit"
                  fullWidth
                  variant="contained"
                  style={{
                    backgroundColor: "#002c54",
                    color: "#fff",
                    margin: "0 0 0 10px",
                    borderRadius: "20px",
                    textTransform: "capitalize",
                    opacity: loading ? 0.8 : 1,
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={22}
                      thickness={4.5}
                      color="inherit"
                    />
                  ) : (
                    "Guardar"
                  )}
                </Button>
              </div>
            </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

export default CreatePatient;
